import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewEncapsulation, Input } from '@angular/core';
import { WorkItemListService } from 'common/workitems/workitem-list.service';
import { WorkItemSms } from 'common/workitems/models/work-item-sms';
import { WorkItemFacebook } from 'common/workitems/models/work-item-facebook';
import { WorkItemAsyncMessage } from 'common/workitems/models/work-item-async-message';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { debounce, find, cloneDeep } from 'lodash';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { BcSettingsService } from 'common/settings/settings.service';
import { PersonType } from 'common/common/constants/person-type.enum';
import { mergeMap, filter, first, takeUntil } from 'rxjs/operators';
import { TextChatItem } from 'common/workitems/interfaces/text-chat.interface';
import { BcConfigService } from 'common/config/provider';
import { UserContextService } from 'common/user-context/user-context.service';
import { WorkItemListenerService } from 'common/workitems/workitem-listener.service';
import { AudioService } from 'common/audio-service/audio.service';
import { IdleStateType } from 'common/workitems/constants/idlestate-types';
import { BcFeatures } from 'common/common/constants/feature-enums';

@Component({
  selector: 'app-work-item-text-chat-list',
  templateUrl: './workitem-text-chat-list-wrapper.component.html',
  styleUrls: ['./workitem-text-chat-list-wrapper.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class WorkItemTextChatListWrapperComponent implements OnInit, OnDestroy {
  @Input()
  isHybrid: boolean;
  public textChatWorkItemList;
  public textChatActiveWorkItemList;
  public textChatPendingWorkItemList;
  public initialInsertIsDone;
  public textChatListInsertSubscription;
  public textChatListRemoveSubscription;
  public textChatListUpdateSubscription;
  public insertWorkItemTextChatsSubscription;
  public workItemType: string = WorkItemTypes.SMS;
  public visitorLanguageCode: string;
  public isIdleStateEnabled: boolean = false;
  private indicateFirstMessageFromNewTextChat: boolean;
  private _updateWorkitemInitialMessagesLoadedDebounced;

  constructor(public userContextService: UserContextService,
              public workItemListService: WorkItemListService,
              public workItemListenerService: WorkItemListenerService,
              public audioService: AudioService,
              private _bcConfig: BcConfigService,
              private _workItemStateHandler: WorkItemStateHandler,
              private _bcSettingsService: BcSettingsService,
              private _changeDetectorRef: ChangeDetectorRef) {

    this.initialInsertIsDone = false;
    this.textChatWorkItemList = this.workItemListService.getTextChatWorkitemList();
    this.textChatActiveWorkItemList = [];
    this.textChatPendingWorkItemList = [];
    this.textChatListInsertSubscription = null;
    this.textChatListRemoveSubscription = null;
    this.textChatListUpdateSubscription = null;
    this.indicateFirstMessageFromNewTextChat = false;
    this._updateWorkitemInitialMessagesLoadedDebounced = debounce(this._updateWorkitemInitialMessagesLoaded, 1000, { leading: false });
  }

  async ngOnInit() : Promise<void> {
    await this._getIdleStateFeatureValue();
    this.createStoreSubscriptionForTextChats();
  }

  ngOnDestroy() {
    this.textChatListInsertSubscription.unsubscribe();
    this.textChatListRemoveSubscription.unsubscribe();
    this.textChatListUpdateSubscription.unsubscribe();
    this.workItemListService.resetTextChats();
  }

  public createStoreSubscriptionForTextChats() {
    this.workItemListenerService.createPromiseForInitialLoadingDone()
      .then(() => this.notifyInitialInsertIsDone());

    this.textChatListInsertSubscription = this.workItemListenerService.createTextChatAcdBasedObservableForInsert()
      .subscribe(workItem => this._onInsertTextChatWorkItem(workItem));

    this.textChatListRemoveSubscription = this.workItemListenerService.createTextChatAcdBasedObservableForRemove()
      .subscribe(workItem => this._onRemoveTextChatWorkItem(workItem));

    this.textChatListUpdateSubscription = this.workItemListenerService.createTextChatAcdBasedObservableForUpdate()
      .subscribe(workItem => this._onUpdateTextChatWorkItem(workItem));

    this._observeTextChatsForWorkItem();
  }

  public getTextChatWorkitemCount(): number {
    return this.workItemListService.getTextChatWorkitemCount();
  }

  public getTextChatActiveWorkitemCount(): number {
    return this.textChatActiveWorkItemList.length;
  }

  public getTextChatPendingWorkitemCount(): number {
    return this.textChatPendingWorkItemList.length;
  }

  public notifyInitialInsertIsDone() {
    this.initialInsertIsDone = true;
    this._sortTextChatWorkItems();
  }

  private _onInsertTextChatWorkItem(workItem: WorkItemSms | WorkItemFacebook | WorkItemAsyncMessage | any) {
    if (this.initialInsertIsDone) {
      this.indicateFirstMessageFromNewTextChat = true;
      this.audioService.playNewTextChatArrived();
    }

    this._setInitialTextChatWorkItem(workItem);
    this._updateActiveAndPendingChatLists();
  }

  private _onUpdateTextChatWorkItem(changes) {
    const workItem = this._getTextChatWorkitemById(changes.id);

    this.workItemListService.updateWorkItem(workItem, changes);
    this._generateWorkitemDepartmentFolder(workItem);
    workItem.NewItem = !workItem.Answered;

    this._sortTextChatWorkItems();

    this._updateTextChatWorkitemState(workItem);
    this._updateParentUnansweredMessageCount();
    this._updateActiveAndPendingChatLists();
  }

  private _onRemoveTextChatWorkItem(workItem) {
    const textChat = this._getTextChatWorkitemById(workItem.id);
    if (textChat) {
      this.workItemListService.stopTimerForItem(textChat);
      this.textChatWorkItemList.splice(this.textChatWorkItemList.indexOf(textChat), 1);
    }

    this.workItemListService.removeWorkItem(workItem);
    this._updateActiveAndPendingChatLists();
  }

  private _observeTextChatsForWorkItem() {
    this.insertWorkItemTextChatsSubscription = this.workItemListenerService.createTextChatAcdBasedObservableForInsert().pipe(
      mergeMap(workItem =>
        this.workItemListenerService.createObservableForWorkItemLastTextChats(workItem).pipe(
          takeUntil(this._observeTextChatWorkItemRemoval(workItem))
        )
      )
    ).subscribe(workItemTextChat => this._insertWorkItemTextChat(workItemTextChat));
  }

  private _observeTextChatWorkItemRemoval(observedWorkItem) {
    return this.workItemListenerService.createTextChatAcdBasedObservableForRemove().pipe(
      filter((workItem: TextChatItem) => workItem.id === observedWorkItem.id),
      first()
    );
  }

  private _updateWorkitemInitialMessagesLoaded(workitemTextChat) {
    const workitem = this._getTextChatWorkitemById(workitemTextChat.ItemID);
    if (workitem && workitemTextChat && workitemTextChat.messages.length) {
      workitem.messageInitialLoadIsFinished = true;
    }
  }

  private _insertWorkItemTextChat(workItemTextChatMessage) {
    this._buildLastMessageObject(workItemTextChatMessage);
    this._updateUnansweredTextChatMessageCount(workItemTextChatMessage);
    this._sendAudioAndPopupNotification(workItemTextChatMessage);
    this._updateWorkitemInitialMessagesLoadedDebounced(workItemTextChatMessage);
    this._setUserLanguageCode(workItemTextChatMessage);
    this._sortMessages(workItemTextChatMessage);
  }

  private _updateUnansweredTextChatMessageCount(workitemTextChat) {
    const numberOfTextChatMessages = workitemTextChat.messages.length;
    const lastTextChatMessage = workitemTextChat.messages[numberOfTextChatMessages - 1];
    const textChatWorkItem = this._getTextChatWorkitemById(workitemTextChat.ItemID);

    if (!textChatWorkItem) {
      return;
    }

    if (lastTextChatMessage.PersonType === PersonType.Operator) {
      textChatWorkItem.unansweredMessages = 0;
    } else {
      if (numberOfTextChatMessages >= textChatWorkItem.unansweredMessages) {
        textChatWorkItem.unansweredMessages += 1;
      }
    }
  }

  private _htmlToPlaintext(text) {
    const strippedText = text && text.replace ? text.replace(/ *<[^>]+> */gm, ' ') : '';
    if (strippedText.length > this._bcConfig.current.lastMessagesTextLength) {
      return strippedText.substring(0, this._bcConfig.current.lastMessagesTextLength);
    }

    return strippedText;
  }

  private _buildLastMessageObject(workitemMessage) {
    const workitem = this._getTextChatWorkitemById(workitemMessage.ItemID);
    if (workitem) {
      workitem.messages = workitemMessage.messages.map(message => {
        message.shortText = this._htmlToPlaintext(message.Text);
        return message;
      });
    }
  }

  private _sendAudioAndPopupNotification(workItemTextChat) {
    if (!this.initialInsertIsDone) {
      return;
    }

    const workitem = this._getTextChatWorkitemById(workItemTextChat.ItemID);
    if (!workitem || !workitem.messageInitialLoadIsFinished && !this.indicateFirstMessageFromNewTextChat) {
      return;
    }
    this.indicateFirstMessageFromNewTextChat = false;
    const numberOfTextChatMessages = workItemTextChat.messages.length;
    const lastTextChatMessage = workItemTextChat.messages[numberOfTextChatMessages - 1];
    if (lastTextChatMessage.PersonType !== PersonType.Operator) {
      this.audioService.playNewTextChatMessageArrived();
    }
  }

  private _updateParentUnansweredMessageCount() {
    if (this.initialInsertIsDone) {
      this.workItemListService.updateParentUnansweredMessageCount();
    }
  }

  private _getTextChatWorkitemById(workitemId): any {
    return find(this.textChatWorkItemList, { ItemID: workitemId });
  }

  private _setInitialTextChatWorkItem(workItem) {
    const item = {
      NewItem: !workItem.Answered,
      UserDisplayName: workItem.Name,
      Name: workItem.Name,
      IsAnswered: this.workItemListService.isAnswered(workItem),
      unansweredMessages: 0,
      secondsRemainingDisplay: '',
      showSecondsRemaining: false,
      secondsRemainingTimerPromise: null,
      messageInitialLoadIsFinished: false,
      IdleState: workItem.IdleState
    };

    this._generateWorkitemDepartmentFolder(workItem);

    workItem.decorateWithDefaultValues(item);

    this.workItemListService.addTimerToItemIfApplicable(workItem);

    this.textChatWorkItemList.push(workItem);

    this._sortTextChatWorkItems();
  }

  private _sortTextChatWorkItems() {
    if (this.initialInsertIsDone) {
      this.textChatWorkItemList.sort(this._workItemStateHandler.textChatComparator.bind(this._workItemStateHandler));
    }
  }

  private _updateTextChatWorkitemState(changes) {
    const workitem = this._getTextChatWorkitemById(changes.id);

    this.workItemListService.stopTimerForItem(workitem);
    this.workItemListService.updateWorkItem(workitem, changes);
    this.workItemListService.addTimerToItemIfApplicable(workitem);
  }

  private _generateWorkitemDepartmentFolder(workitem) {
    const departmentFolder = [];
    if (workitem) {
      if (workitem.FolderName) {
        departmentFolder.push(workitem.FolderName);
      }
      if (workitem.DepartmentName) {
        departmentFolder.push(workitem.DepartmentName);
      }
      workitem.departmentFolder = departmentFolder.join(', ');
    }
  }

  private _sortMessages(workItemMessage): void {
    const workitem = this._getTextChatWorkitemById(workItemMessage.ItemID);
    workitem.messages.sort(WorkItemStateHandler.compareObjectsByCreatedDateAsc);
  }

  private _updateActiveAndPendingChatLists() {
    this.textChatActiveWorkItemList = this.textChatWorkItemList.filter(workItem => workItem.IdleState === IdleStateType.ACTIVE);
    this.textChatPendingWorkItemList = this.textChatWorkItemList.filter(workItem => workItem.IdleState === IdleStateType.PENDING);
  }

  private _getLastMessage(messages) {
    const mssgs = cloneDeep(messages).reverse();

    return mssgs.find(message => message.PersonType === PersonType.Visitor);
  }

  private _setUserLanguageCode(workItemTextChatMessage) {
    const lastUserMessage = this._getLastMessage(workItemTextChatMessage.messages);

    if (lastUserMessage) {
      this.visitorLanguageCode = lastUserMessage.LanguageCode;
    }
  }

  private _getIdleStateFeatureValue(): Promise<void> {
    return this._bcSettingsService.getSettings().then(settings => {
      this.isIdleStateEnabled = this._bcSettingsService.AsyncMessageFeatures.isEnabled(settings, BcFeatures.AM_INACTIVE_STATE_HANDLING);
      this._changeDetectorRef.detectChanges();
    });
  }
}
